/**
 * The shell around a mounted collaborative document.
 *
 * Five files render this surface -- the docs route, the extension mount, the
 * two harness routes and `CollabDocumentSurface` itself -- and they all have to
 * agree, because whichever one paints first is what the reader sees while the
 * Y.Doc resolves. Named here rather than repeated at each call site, which is
 * how the harness routes drifted from the real route in the first place.
 *
 * Kebab-case markers are kept alongside the utilities: `styles.css` still owns
 * the few rules that cannot be a utility (the editor's own measure, the tracker
 * detail's inset), and they select on these.
 */

/**
 * A column, so the editor grows into whatever height the shell gives it. As a
 * fixed block it left a band of dead page below the document on a tall window.
 *
 * `flex-1` is load-bearing and was missing until a real board was opened.
 * Without it this section is `flex: 0 1 auto` and sizes to its content, while
 * its only child `.collab-editor-mount` is `flex-1` with a `0%` basis. That is
 * fine for an editor with an intrinsic measure of its own -- Lexical resolves
 * to the full pane and the section follows it -- and it collapses the whole
 * chain to **zero height** for any editor sized in percentages of its parent.
 * Project Canvas is exactly that: React Flow asks for `height: 100%` of a
 * parent that is 0, so a shared board mounted, bound to its Y.Doc, rendered all
 * twelve of its nodes, and painted a 0px-tall pane. It read as "canvas does not
 * load on the web" for as long as canvas has been on the web.
 *
 * The same collapse was already patched once, locally, by the
 * `min-height: 320px` rule `styles.css` puts on this class inside the tracker
 * detail. That was the workaround; this is the cause.
 *
 * Measured on a real signed-in document before changing it: markdown is 897px
 * either way, so growing this section moves nothing that was already working.
 */
export const EDITOR_SURFACE =
  'collab-editor-surface flex min-h-0 flex-1 flex-col overflow-hidden border-0 bg-nim';

export const EDITOR_LOADING =
  'collab-editor-loading grid min-h-0 flex-1 place-items-center text-nim-muted';

/**
 * The fallback status strip, for the harness routes that have no breadcrumb bar
 * to host it. In the console it lives beside the breadcrumb instead.
 */
export const EDITOR_STATUS_STRIP =
  'collab-editor-status flex items-center justify-end border-b border-nim bg-nim-secondary px-[0.8rem] py-2';

/**
 * The dead ends a reader actually lands on -- an 8 MiB document, a revoked
 * write, contents that never arrived. Centered in the editor area they replace,
 * sized for prose, with the escape hatch as a real button, so they read as a
 * considered explanation rather than a bare alert.
 */
const EDITOR_NOTICE_BASE =
  'm-[0.8rem] grid justify-items-center gap-[0.6rem] rounded-[0.8rem] border px-8 py-10 text-center '
  + '[&>strong]:text-base [&>span]:max-w-[34rem] [&>span]:leading-normal [&>span]:text-nim-muted';

export const EDITOR_NOTICE = `${EDITOR_NOTICE_BASE} border-nim bg-nim-secondary`;

/** The same block, tinted from `--nim-error` for a refusal rather than a limit. */
export const EDITOR_NOTICE_ERROR =
  `${EDITOR_NOTICE_BASE} border-[var(--nim-error)] `
  + 'bg-[color-mix(in_srgb,var(--nim-error)_12%,transparent)] [&>strong]:text-[var(--nim-error)]';

/**
 * The size-limit block. Tinted over the page background rather than over
 * whatever is behind it, so the document text does not show through.
 */
export const EDITOR_NOTICE_LIMIT =
  `${EDITOR_NOTICE_BASE} border-[var(--nim-error)] [&>strong]:text-nim `
  + 'bg-[linear-gradient(color-mix(in_srgb,var(--nim-error)_12%,transparent),color-mix(in_srgb,var(--nim-error)_12%,transparent)),var(--nim-bg)]';

/** The standalone harness pages, which are a page rather than a mode surface. */
export const HARNESS_PAGE = 'collab-harness-page min-h-screen bg-nim p-8';
